import React, { ReactNode, useState } from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import { FileUploadModal } from '../../../../shared/common/FileUploadModal/FileUploadModal';
import LoadingOverlay from '../../../../shared/components/LoadingOverlay';
import { LoadingWrapper } from '../../../../shared/components/LoadingWrapper';
import MaterialIcon from '../../../../shared/common/MaterialIcon';
import { ContainerInnerWithColumns } from '../../../../shared/common/ContainerInner';
import { showNotification, TYPE_ERROR } from '../../../../store/notifications/actions';
import { extractStateObject } from '../../../../shared/utils/utils-localcontent';
import HorizontalRuler from '../../../../shared/common/HorizontalRuler';
import { LCFormFieldValueText } from '../../shared/LCFormFieldValueText';
import { LCFormField } from '../../shared/LCFormField';
import { hasLCManageRole } from '../../../../store/authentication/actions';
import ConfirmationModalLayout from '../../../../shared/components/modal/ConfirmationModalLayout';
import { LibxSettingsPopup } from '../publishing/LibxSettingsPopup';
import LCStatusActions from '../LCStatusActions';
import LCStatusIndicator from '../LCStatusIndicator';
import { LCPublishWorkflow } from '../publishing/LCPublishWorkflow';
import LCProductDetailAssetsButton from '../LCProductDetailAssetsButton';
import { LCProductDetailAssetEbook } from '../LCProductDetailAssetEbook';
import { LCProductDetailAssetCover } from '../LCProductDetailAssetCover';
import { URL_LIBX_ROUTE, URL_SUFFIX_LIBX_PRODUCT_DETAILS } from '../../../libx/constants';
import {
    StDetailViewWrapper,
    StInfoSharedCollectionTitle,
    StLCProductDetailAssetsWrapper,
    StMetadataTitleWrapper,
    StRightAlignedContainer,
    StTitleButtonWrapper,
    StTitleStatusWrapper,
    StViewInLibxLink
} from '../styled/LCStyledComponents';
import {
    CountryOut,
    LibraryCreatedProductOut,
    LibXchangeSharingOptions,
    PublishOptions
} from '../../../../store/apiLibx';
import { LCProductDetailReadMetaData } from './LCProductDetailReadMetadata';
import { useDispatch, useSelector } from 'react-redux';
import { AuthenticationState } from '../../../../store/authentication/state';
import { useHistory } from 'react-router';
import { AppState } from '../../../../store/appState';
import { UploadProgressCallback } from '../../../../api/local-content/collection/product/useUploadLocalContentAsset';
import { useTranslation } from 'react-i18next';

const spaceBetweenBlocksInPx = 130;

interface Props {
    product: LibraryCreatedProductOut;
    publishProduct: (id: string, publishingOptions: PublishOptions) => void;
    authentication: AuthenticationState;
    onSaveLibxSettings: (productId: string, sharingOptions: LibXchangeSharingOptions) => void;
    onSetLiveOnLibx: (productId: string, sharingOptions: LibXchangeSharingOptions) => void;
    onDeleteCover: (productId: string) => void;
    onWithdrawProduct: (productId: string) => void;
    onDeactivateProduct: (productId: string) => void;
    republishProduct: (productId: string) => void;
    onDeleteProduct: (productId: string) => void;
    countries: CountryOut[];
    onUploadNewCover: (productId: string, file: File, callback: UploadProgressCallback, closeDialog) => void;
    onUploadNewAsset: (productId: string, file: File, callback: UploadProgressCallback, closeDialog) => void;
    onReactivateProduct: (productId: string) => void;
    loading: boolean;
    processing?: boolean;
}

export const LCProductDetailReadView = (props: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { push, location } = useHistory();
    const [showPublishWorkflow, setShowPublishWorkflow] = useState(false);
    const [showCoverUploadDialog, setShowCoverUploadDialog] = useState(false);
    const [showAssetUploadDialog, setShowAssetUploadDialog] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showWithdrawConfirmation, setShowWithdrawConfirmation] = useState(false);
    const [showSetLiveLibXchangeModal, setShowSetLiveLibXchangeModal] = useState(false);
    const [showDeactivateConfirmation, setShowDeactivateConfirmation] = useState(false);
    const [showLibxSettingsModal, setShowLibxSettingsModal] = useState(false);
    const spocIdPublisherProduct = props.product.spocId;
    const spocIdCurrentUser = props.authentication.spoc.id;

    const isReadOnly = spocIdPublisherProduct !== spocIdCurrentUser;

    function handlePublishProduct(publishingOptions) {
        props.publishProduct(props.product.id ?? '', publishingOptions);
        setShowPublishWorkflow(false);
    }

    function closeCoverUploadDialog() {
        setShowCoverUploadDialog(false);
    }

    function closeAssetUploadDialog() {
        setShowAssetUploadDialog(false);
    }

    function handleOnSaveLibxSettings(productId, sharingOptions, countryIsModified) {
        props.onSaveLibxSettings(productId, sharingOptions);
        setShowLibxSettingsModal(false);
        if (countryIsModified) {
            dispatch(showNotification(t('yourLocalContent.yourTitles.warnings.distributionChanged')));
        }
    }

    function handleOnSetLiveOnLibx(productId, sharingOptions) {
        props.onSetLiveOnLibx(productId, sharingOptions);
        setShowSetLiveLibXchangeModal(false);
    }

    function showFileRejectedError(text) {
        dispatch(showNotification(text, TYPE_ERROR));
    }

    let disabled = false;
    let statusIndicator: ReactNode = null;
    let statusActions: ReactNode = null;
    let infoSharedCollectionTitle: ReactNode = null;
    const isEditMode = false;
    const userHasLCManageRole = hasLCManageRole(props.authentication);
    const statusActionsDisabled = isEditMode || !userHasLCManageRole;
    const activeStatus = props.product.activeStatus ?? [];
    const stateList = extractStateObject(activeStatus);
    const liveLibx = props.product.liveOnLibXchange;
    const shareLibx = props.product.shareOnLibXchange;

    if (stateList !== null) {
        const { newState, updated, processing, liveCollection, inactive } = stateList;
        disabled = inactive || isEditMode;

        statusIndicator = (
            <LCStatusIndicator
                newUpdated={newState || updated || processing}
                processing={processing}
                liveCollection={liveCollection}
                inactive={inactive}
                liveLibx={liveLibx ?? false}
                disabled={isEditMode}
            />
        );

        statusActions = isReadOnly ? null : (
            <LCStatusActions
                newState={newState}
                updated={updated}
                liveCollection={liveCollection}
                inactive={inactive}
                liveLibx={liveLibx ?? false}
                shareLibx={shareLibx ?? false}
                productId={props.product.id ?? ''}
                onSendUpdatesClick={props.republishProduct}
                onSetLiveClick={() => setShowPublishWorkflow(true)}
                onActivateTitleClick={props.onReactivateProduct}
                onDeleteTitleClick={() => setShowDeleteConfirmation(true)}
                onDeactivateTitleClick={() => setShowDeactivateConfirmation(true)}
                onWithdrawLibXchange={() => setShowWithdrawConfirmation(true)}
                onSetLiveLibXchange={() => setShowSetLiveLibXchangeModal(true)}
                disabled={statusActionsDisabled}
                isAssetUploaded={!isNil(props.product.assetFileName)}
            />
        );

        infoSharedCollectionTitle = !isReadOnly ? null : (
            <StInfoSharedCollectionTitle>Shared Collection Title</StInfoSharedCollectionTitle>
        );
    }

    const isNew = false;
    const titleText = isNew ? t('yourLocalContent.yourTitles.newTitle') : props.product.titleWithoutPrefix;
    const countryCode = useSelector((state: AppState) => state.authentication.spoc.taxCountry);
    const selectedCountries = props.product.libXchangeCountryCodes;
    const distributionDescription = isEmpty(selectedCountries)
        ? t('yourLocalContent.yourTitles.worldwide')
        : t('yourLocalContent.yourTitles.limited');
    const libxTerritoryEditDisabled = disabled || !userHasLCManageRole;

    const libxRoute = `/${URL_LIBX_ROUTE}/${URL_SUFFIX_LIBX_PRODUCT_DETAILS}/${props.product.productId}`;

    return (
        <ContainerInnerWithColumns>
            <LoadingWrapper>
                <LoadingOverlay loading={props.loading} />
            </LoadingWrapper>
            <ConfirmationModalLayout
                visible={showDeleteConfirmation}
                text={t('yourLocalContent.yourTitles.confirmations.deleteConfirmation')}
                title={t('yourLocalContent.yourTitles.confirmations.delete', {
                    titleWithoutPrefix: props.product.titleWithoutPrefix
                })}
                onConfirm={() => props.onDeleteProduct(props.product.id ?? '')}
                onCancel={() => setShowDeleteConfirmation(false)}
            />
            <ConfirmationModalLayout
                visible={showWithdrawConfirmation}
                text={t('yourLocalContent.yourTitles.confirmations.withdrawConfirmation')}
                title={t('yourLocalContent.yourTitles.confirmations.withdraw', {
                    titleWithoutPrefix: props.product.titleWithoutPrefix
                })}
                onConfirm={() => {
                    props.onWithdrawProduct(props.product.id ?? '');
                    setShowWithdrawConfirmation(false);
                }}
                onCancel={() => setShowWithdrawConfirmation(false)}
            />
            <ConfirmationModalLayout
                visible={showDeactivateConfirmation}
                text={t('yourLocalContent.yourTitles.confirmations.deactiveConfirmation')}
                title={t('yourLocalContent.yourTitles.confirmations.deactive', {
                    titleWithoutPrefix: props.product.titleWithoutPrefix
                })}
                onConfirm={() => {
                    props.onDeactivateProduct(props.product.id ?? '');
                    setShowDeactivateConfirmation(false);
                }}
                onCancel={() => setShowDeactivateConfirmation(false)}
            />
            <LibxSettingsPopup
                show={showLibxSettingsModal}
                countries={props.countries}
                title={t('yourLocalContent.yourTitles.libxChangeSettings')}
                defaultCountryCode={countryCode}
                selectedCountries={selectedCountries ?? []}
                productId={props.product.id ?? ''}
                onFinish={handleOnSaveLibxSettings}
                onAbort={() => setShowLibxSettingsModal(false)}
            />
            <LibxSettingsPopup
                show={showSetLiveLibXchangeModal}
                countries={props.countries}
                title={t('yourLocalContent.yourTitles.libxChangeLive')}
                defaultCountryCode={countryCode}
                selectedCountries={selectedCountries ?? []}
                productId={props.product.id ?? ''}
                onFinish={handleOnSetLiveOnLibx}
                onAbort={() => setShowSetLiveLibXchangeModal(false)}
            />
            <LCPublishWorkflow
                authentication={props.authentication}
                countries={props.countries ?? []}
                show={showPublishWorkflow}
                onAbort={() => setShowPublishWorkflow(false)}
                onPublish={handlePublishProduct}
                spocId={props.authentication.spoc.id}
            />
            <StTitleStatusWrapper>
                <div>
                    <a className="fl-left" onClick={() => window.history.back()}>
                        <i className="material-icons" style={{ fontSize: '28px' }}>
                            arrow_back
                        </i>
                    </a>
                    <span style={{ fontSize: '20px' }}>{titleText}</span>
                </div>
                {statusIndicator}
                {statusActions}
                {infoSharedCollectionTitle}
            </StTitleStatusWrapper>
            <StDetailViewWrapper className="LCProductDetailView" assetWrapperHidden={isEditMode}>
                <div>
                    <StTitleButtonWrapper>
                        <StMetadataTitleWrapper>
                            <LCProductDetailAssetsButton
                                readOnly={false}
                                role="edit"
                                name="Metadata"
                                buttonText={t('yourLocalContent.yourTitles.editMetaData')}
                                onClick={() => push(`${location.pathname}/edit`)}
                                disabled={disabled}
                            />
                        </StMetadataTitleWrapper>
                    </StTitleButtonWrapper>
                    <LCProductDetailReadMetaData product={props.product} />
                </div>
                <StRightAlignedContainer>
                    <StLCProductDetailAssetsWrapper>
                        <StTitleButtonWrapper>
                            <LCProductDetailAssetsButton
                                readOnly={isReadOnly}
                                name={t('yourLocalContent.yourTitles.cover')}
                                role="fileUpload"
                                buttonText={t('yourLocalContent.yourTitles.uploadCoverFile')}
                                tooltipText={t('yourLocalContent.yourTitles.tooltips.coverFile')}
                                onClick={() => setShowCoverUploadDialog(true)}
                                showDeleteButton={props.product.defaultCover !== true}
                                onDelete={() => props.onDeleteCover(props.product.id ?? '')}
                            >
                                <FileUploadModal
                                    show={showCoverUploadDialog}
                                    onClose={() => closeCoverUploadDialog()}
                                    onAbort={() => closeCoverUploadDialog()}
                                    accept={{
                                        'image/png': ['.png'],
                                        'image/jpeg': ['.jpeg', '.jpg'],
                                        'image/tiff': ['.tif', '.tiff']
                                    }}
                                    allowedFileFormatLabel={t(
                                        'yourLocalContent.yourTitles.allowedFileFormatLabelCover'
                                    )}
                                    title={t('yourLocalContent.yourTitles.uploadCover')}
                                    description={t('yourLocalContent.yourTitles.tooltips.coverFile')}
                                    onUpload={(fileToUpload, uploadProgressCallback) => {
                                        props.onUploadNewCover(
                                            props.product.id ?? '',
                                            fileToUpload,
                                            uploadProgressCallback,
                                            closeCoverUploadDialog
                                        );
                                    }}
                                    showFileRejectedError={showFileRejectedError}
                                />
                            </LCProductDetailAssetsButton>
                        </StTitleButtonWrapper>
                        <LCProductDetailAssetCover
                            coverUrl={props.product.coverUrl ?? ''}
                            productTitle={props.product.titleWithoutPrefix ?? ''}
                        />
                        <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                        <StTitleButtonWrapper>
                            <LCProductDetailAssetsButton
                                readOnly={isReadOnly}
                                name={t('yourLocalContent.yourTitles.ebookFile')}
                                role="fileUpload"
                                buttonText={t('yourLocalContent.yourTitles.uploadEbookFile')}
                                tooltipText={t('yourLocalContent.yourTitles.tooltips.ebookFile')}
                                onClick={() => setShowAssetUploadDialog(true)}
                            >
                                <FileUploadModal
                                    show={showAssetUploadDialog}
                                    onClose={() => closeAssetUploadDialog()}
                                    onAbort={() => closeAssetUploadDialog()}
                                    accept={{ 'application/epub+zip': ['.epub'], 'application/pdf': ['.pdf'] }}
                                    allowedFileFormatLabel={t(
                                        'yourLocalContent.yourTitles.allowedFileFormatLabelEbook'
                                    )}
                                    description={t('yourLocalContent.yourTitles.tooltips.ebookFile')}
                                    title={t('yourLocalContent.yourTitles.uploadEbookFile')}
                                    onUpload={(fileToUpload, uploadProgressCallback) => {
                                        props.onUploadNewAsset(
                                            props.product.id ?? '',
                                            fileToUpload,
                                            uploadProgressCallback,
                                            closeAssetUploadDialog
                                        );
                                    }}
                                    showFileRejectedError={showFileRejectedError}
                                />
                            </LCProductDetailAssetsButton>
                        </StTitleButtonWrapper>
                        <LCProductDetailAssetEbook fileName={props.product.assetFileName} />

                        {props.product.liveOnLibXchange ? (
                            <React.Fragment>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                                <StTitleButtonWrapper>
                                    <LCProductDetailAssetsButton
                                        readOnly={isReadOnly}
                                        name={t('yourLocalContent.yourTitles.libxChangeSettings')}
                                        role="edit"
                                        buttonText={t('yourLocalContent.yourTitles.editSettings')}
                                        //tooltipText={props.tooltips.LIBXchange}  TODO: Add libxChange Tooltip
                                        disabled={libxTerritoryEditDisabled}
                                        onClick={() => setShowLibxSettingsModal(true)}
                                    />
                                    <StViewInLibxLink className="material-link" to={libxRoute}>
                                        <span>{t('yourLocalContent.yourTitles.viewInLibxChange')}</span>
                                        <MaterialIcon icon="keyboard_arrow_right" />
                                    </StViewInLibxLink>

                                    <LCFormField
                                        title={t('yourLocalContent.yourTitles.distribution')}
                                        isEdit={false}
                                        viewComponent={<LCFormFieldValueText text={distributionDescription} />}
                                    />
                                </StTitleButtonWrapper>
                            </React.Fragment>
                        ) : null}
                    </StLCProductDetailAssetsWrapper>
                </StRightAlignedContainer>
            </StDetailViewWrapper>
        </ContainerInnerWithColumns>
    );
};
